import { Component, OnInit, OnDestroy, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { VerticalBarSimpleChart } from '../vertical-bar-simple-chart/vertical-bar-simple-chart';
import { HorizontalBarDotsChart } from '../horizontal-bar-dots-chart/horizontal-bar-dots-chart';
import { XRayDealerReportsService } from '../../services/x-ray-dealer-reports-service';
import { ExerciseCalendar } from '../shared/exercise-calendar/exercise-calendar';
import { ExportDocumentButton } from '../../export-document-button/export-document-button';
import { SelectedDnService } from '../../services/selected-dn.service';
import { ReportService } from '../../services/report.service';
import { ReportPageContextService } from '../../services/report-page-context.service';

import { TrackClickDirective } from '../../directives/track-click.directive';
import { ExportDownloadModal } from '../export-download-modal/export-download-modal';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-raio-x-dealer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    VerticalBarSimpleChart,
    HorizontalBarDotsChart,
    ExerciseCalendar,
    ExportDocumentButton,
    TrackClickDirective,
    ExportDownloadModal
  ],
  templateUrl: './raio-x-dealer.html',
  styleUrls: ['./raio-x-dealer.scss']
})
export class RaioXDealer implements OnInit, OnDestroy {
  private dnSubscription?: Subscription;

  @ViewChild('reportContainer') reportContainer!: ElementRef<HTMLElement>;
  @ViewChild('pdfContent') pdfContent!: ElementRef<HTMLElement>;

  // Dados que virão da API
  apiData: any;
  loading = true;
  error = false;
  errorMessage: string | null = null;
  is404Error: boolean = false;
  concessionariaNome = 'Nome Concessionária';
  selectedFilter: string = 'VAREJO';

  // Propriedades para controle de período - INICIALIZAR COMO NULL
  selectedYear: number | null = null;
  selectedMonth: number | null = null;

  // Cache para armazenar os dados
  private dataCache: { [key: string]: any } = {};

  closedDate: string = '';
  availableExercicios: string[] = [];
  exportLoading = false;

  // Controle do modal de exportação
  showExportModal = false;
  exportFileName = '';
  exportFileBlob: Blob | null = null;
  exportEmailSubject = '';
  exportEmailBody = '';

  constructor(
    private xRayDealerService: XRayDealerReportsService,
    private selectedDnService: SelectedDnService,
    private reportService: ReportService,
    private reportPageContext: ReportPageContextService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    // Carrega a data de fechamento
    this.loadClosedDate();

    // Subscreve às mudanças do DN
    this.dnSubscription = this.selectedDnService.selectedDn$.subscribe(dn => {
      if (dn) {
        // Limpar cache quando DN mudar para forçar recarregamento
        this.dataCache = {};
        // Recarregar dados quando DN mudar
        this.loadDataWithCurrentFilters();
        // Forçar detecção de mudanças
        this.cdr.detectChanges();
      }
    });
  }

  ngOnDestroy(): void {
    // Limpa a subscription para evitar memory leaks
    if (this.dnSubscription) {
      this.dnSubscription.unsubscribe();
    }
  }

  loadClosedDate(): void {
    this.reportService.getClosedDate('Raio X').subscribe({
      next: (date: string) => {
        this.closedDate = date || '';
      },
      error: (error) => {
        console.error('Erro ao carregar data de fechamento:', error);
        this.closedDate = '';
      }
    });
  }

  // Função para obter o nome do mês - com tratamento para null
  getMonthName(monthNumber: number | null): string {
    if (!monthNumber) return '';

    const months = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    return months[monthNumber - 1] || '';
  }

  // Função para obter o nome da categoria - com tratamento para null/undefined
  getCategoryName(category: string | null | undefined): string {
    if (!category) return '';

    const categories: { [key: string]: string } = {
      'VAREJO': 'Varejo',
      'FROTA': 'Frota'
    };
    return categories[category] || category;
  }

  // MÉTODO PARA MUDANÇA DE PERÍODO (chamado pelo componente exercise-calendar)
  onPeriodChange(period: { year: number; month: number }) {
    this.selectedYear = period.year;
    this.selectedMonth = period.month;

    this.loadDataWithCurrentFilters();
  }

  // MÉTODO PARA CARREGAR DADOS COM FILTROS ATUAIS
  loadDataWithCurrentFilters() {
    this.loadXRayData(this.selectedFilter);
  }

  // MÉTODOS EXISTENTES
  setFilter(filter: string) {
    if (this.selectedFilter !== filter) {
      this.selectedFilter = filter;
      this.loadDataWithCurrentFilters();
    }
  }

  loadXRayData(tipoSegmento: string) {
    this.loading = true;
    this.error = false;
    this.errorMessage = null;
    this.is404Error = false;

    // Se não tem mês selecionado, envia null para anoExercicio
    let anoExercicio: number | null = null;

    if (this.selectedMonth !== null && this.selectedYear !== null) {
      const periodoStr = `${this.selectedYear}${this.selectedMonth.toString().padStart(2, '0')}`;
      anoExercicio = parseInt(periodoStr, 10);
    }

    const currentDn = this.selectedDnService.getSelectedDnAsNumber();
    const cacheKey = `${currentDn}_${tipoSegmento}_${anoExercicio || 'initial'}`;

    if (this.dataCache[cacheKey]) {
      this.apiData = this.dataCache[cacheKey].data;
      this.availableExercicios = this.dataCache[cacheKey].availableExercicios || [];
      this.updateConcessionariaNome();
      this.loading = false;
      this.error = false;
      this.errorMessage = null;
      this.is404Error = false;
    } else {
      this.xRayDealerService.getXRayDataOnly(currentDn, anoExercicio, tipoSegmento).subscribe({
        next: (response) => {
          this.apiData = response.data;
          this.availableExercicios = response.availableExercicios || [];
          this.dataCache[cacheKey] = { data: response.data, availableExercicios: response.availableExercicios };
          this.updateConcessionariaNome();
          this.loading = false;
          this.error = false;
          this.errorMessage = null;
          this.is404Error = false;

          // Forçar detecção de mudanças após carregar dados
          this.cdr.detectChanges();
        },
        error: (error: any) => {
          console.error('Erro ao carregar dados:', error);
          this.loading = false;
          this.error = true;

          // Verifica se é erro 404
          if (error?.status === 404) {
            this.is404Error = true;
            this.errorMessage = 'Informação não encontrada para o exercício selecionado!';
          } else {
            this.is404Error = false;
            this.errorMessage = 'Ocorreu um erro no processamento da requisição! Tente novamente mais tarde e/ou procure um administrador!';
          }
          this.cdr.detectChanges();
        }
      });
    }
  }

  // MÉTODO PARA ATUALIZAR O NOME DA CONCESSIONÁRIA
  private updateConcessionariaNome(): void {
    if (this.apiData && this.apiData.nM_GRP_COMISSION) {
      this.concessionariaNome = this.apiData.nM_GRP_COMISSION;
    } else {
      this.concessionariaNome = 'Nome Concessionária';
    }
  }

  // MÉTODO PARA LIDAR COM EXPORTAÇÃO SOLICITADA
  onExportRequested(period: {
    start: { year: number; month: number };
    end: { year: number; month: number }
  }): void {
    const dn = this.selectedDnService.getSelectedDnAsNumber();
    if (dn == null || dn === undefined || Number.isNaN(dn)) {
      this.errorMessage = 'Selecione um DN para exportar.';
      this.cdr.detectChanges();
      return;
    }

    this.exportLoading = true;
    this.errorMessage = null;
    this.cdr.detectChanges();

    this.xRayDealerService.getDealerExportData(period, dn).subscribe({
      next: (res) => {
        try {
          const pad = (n: number) => n.toString().padStart(2, '0');
          const allRows = (res.data || []).flatMap((d: { exercicio: string; items: any[] }) =>
            (d.items || []).map((item: any) => ({ Exercicio: d.exercicio, ...item }))
          );

          const wb = XLSX.utils.book_new();
          const ws = XLSX.utils.json_to_sheet(allRows);
          XLSX.utils.book_append_sheet(wb, ws, 'Raio-X-Dealer');

          const fileName = `raio-x-dealer-${dn}-${period.start.year}${pad(period.start.month)}-${period.end.year}${pad(period.end.month)}.xlsx`;
          XLSX.writeFile(wb, fileName);
        } catch (e) {
          console.error('Erro ao gerar Excel:', e);
          this.errorMessage = 'Erro ao gerar o arquivo Excel. Tente novamente.';
          this.cdr.detectChanges();
        }
        this.exportLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Erro ao exportar dados:', err);
        this.errorMessage = err?.error?.message || 'Erro ao buscar dados para exportação. Tente novamente.';
        this.exportLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  // MÉTODO PARA LIDAR COM CANCELAMENTO DE EXPORTAÇÃO
  onExportCancelled(): void {
    console.log('Exportação cancelada');
  }

  // MÉTODO AUXILIAR PARA NOME CURTO DO MÊS
  private getShortMonthName(month: number): string {
    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return monthNames[month - 1] || '';
  }

  // MÉTODO PARA OBTER ÍCONE DE SETA DINAMICAMENTE PARA ISR E PENETRATION
  getArrowIcon(column: 'isr' | 'penetration', comparator: 'regional' | 'national'): string {
    if (!this.apiData) {
      return 'assets/icons/arrowRightYellow.png';
    }

    const dealerValue = column === 'isr' ? this.apiData.isR_DN : this.apiData.penetratioN_REF_DN;
    let referenceValue: number;

    if (comparator === 'regional') {
      referenceValue = column === 'isr' ? this.apiData.isR_REF_REG : this.apiData.penetratioN_REF_REG;
    } else {
      referenceValue = column === 'isr' ? this.apiData.isR_REF_NAC : this.apiData.penetratioN_REF_NAC;
    }

    // Comparação: dealerValue com referenceValue
    if (dealerValue > referenceValue) {
      return 'assets/icons/arrowUpGreen.png';
    } else if (Math.abs(dealerValue - referenceValue) < 0.01) { // Considera igual se diferença menor que 0.01
      return 'assets/icons/arrowRightYellow.png';
    } else {
      return 'assets/icons/arrowDownRed.png';
    }
  }

  // MÉTODO PARA OBTER ÍCONE DE SETA DINAMICAMENTE PARA VALORES FINANCIADOS E COMISSÕES
  getValueArrowIcon(type: 'valor' | 'comissao', comparator: 'regional' | 'national'): string {
    if (!this.apiData) {
      return 'assets/icons/arrowRightYellow.png';
    }

    let dealerValue: number;
    let referenceValue: number;

    if (type === 'valor') {
      dealerValue = this.apiData.vaL_FINANC_ANO_ATUAL_DN;
      referenceValue = comparator === 'regional'
        ? this.apiData.vaL_FINANC_ANO_ATUAL_REG
        : this.apiData.vaL_FINANC_ANO_ATUAL_NAC;
    } else {
      dealerValue = this.apiData.coM_PAGAS_ANO_ATUAL_DN;
      referenceValue = comparator === 'regional'
        ? this.apiData.coM_PAGAS_ANO_ATUAL_REG
        : this.apiData.coM_PAGAS_ANO_ATUAL_NAC;
    }

    // Calcula o percentual
    const percentual = (dealerValue / referenceValue) * 100;

    if (percentual > 100) {
      return 'assets/icons/arrowUpGreen.png';
    } else if (Math.abs(percentual - 100) < 0.01) {
      return 'assets/icons/arrowRightYellow.png';
    } else {
      return 'assets/icons/arrowDownRed.png';
    }
  }

  // MÉTODO PARA OBTER O PERCENTUAL PARA VALORES FINANCIADOS E COMISSÕES
  getValuePercentage(type: 'valor' | 'comissao', comparator: 'regional' | 'national'): string {
    if (!this.apiData) {
      return '0%';
    }

    let dealerValue: number;
    let referenceValue: number;

    if (type === 'valor') {
      dealerValue = this.apiData.vaL_FINANC_ANO_ATUAL_DN;
      referenceValue = comparator === 'regional'
        ? this.apiData.vaL_FINANC_ANO_ATUAL_REG
        : this.apiData.vaL_FINANC_ANO_ATUAL_NAC;
    } else {
      dealerValue = this.apiData.coM_PAGAS_ANO_ATUAL_DN;
      referenceValue = comparator === 'regional'
        ? this.apiData.coM_PAGAS_ANO_ATUAL_REG
        : this.apiData.coM_PAGAS_ANO_ATUAL_NAC;
    }

    // Se o valor de referência for 0, retorna 0%
    if (referenceValue === 0) {
      return '0%';
    }

    // Calcula a diferença percentual: (dealerValue - referenceValue) / referenceValue * 100
    const percentDiff = ((dealerValue - referenceValue) / referenceValue) * 100;

    // Formata para 1 casa decimal e adiciona o sinal de + se positivo
    const formattedDiff = Math.abs(percentDiff).toFixed(1);
    return percentDiff >= 0 ? `${formattedDiff}%` : `${formattedDiff}%`;
  }

  // ... (seus getters permanecem os mesmos, mas atualizei para usar this.concessionariaNome)
  get veiculosFaturadosData() {
    return this.apiData ? [
      this.apiData.veiC_FAT_AAK_DN,
      this.apiData.veiC_FAT_AAK_REG,
      this.apiData.veiC_FAT_AAK_NAC
    ] : [0, 0, 0];
  }

  get veiculosFaturadosLabels() {
    return [this.concessionariaNome, 'Média Regional', 'Média Nacional'];
  }

  get veiculosFaturadosColors() {
    return ['#001E50', '#00B0F0', '#6A767D'];
  }

  get horizontalBarData() {
    if (!this.apiData) return [];
    return [
      {
        name: 'Garantia',
        values: [this.apiData.pL_GARANTIA_REF_DN, this.apiData.pL_GARANTIA_REF_REG, this.apiData.pL_GARANTIA_REF_NAC],
        value: 500,
        valuePositions: ['top', 'bottom', 'top']
      },
      {
        name: 'Seguro Casco',
        values: [this.apiData.pL_SEGURO_CASCO_REF_DN, this.apiData.pL_SEGURO_CASCO_REF_REG, this.apiData.pL_SEGURO_CASCO_REF_NAC],
        value: 500,
        valuePositions: ['bottom', 'top', 'bottom']
      },
      {
        name: 'Revisões Planejadas',
        values: [this.apiData.pL_REVISOES_PLANEJADAS_REF_DN, this.apiData.pL_REVISOES_PLANEJADAS_REF_REG, this.apiData.pL_REVISOES_PLANEJADAS_REF_NAC],
        value: 500,
        valuePositions: ['top', 'bottom', 'top']
      }
    ];
  }

  get isrData() {
    if (!this.apiData) {
      return {
        dealer: { name: this.concessionariaNome, percentage: '0' },
        regional: { name: 'Referência Regional', percentage: '0' },
        national: { name: 'Referência Nacional', percentage: '0' },
        indiceComissao: '0'
      };
    }

    return {
      dealer: { name: this.concessionariaNome, percentage: this.apiData.isR_DN.toFixed(1) },
      regional: { name: 'Referência Regional', percentage: this.apiData.isR_REF_REG.toFixed(1) },
      national: { name: 'Referência Nacional', percentage: this.apiData.isR_REF_NAC.toFixed(1) },
      indiceComissao: this.apiData.indicE_DE_COMISSAO.toFixed(2)
    };
  }

  get penetrationData() {
    if (!this.apiData) {
      return {
        dealer: { name: this.concessionariaNome, percentage: '0' },
        regional: { name: 'Referência Regional', percentage: '0' },
        national: { name: 'Referência Nacional', percentage: '0' }
      };
    }

    return {
      dealer: { name: this.concessionariaNome, percentage: this.apiData.penetratioN_REF_DN.toFixed(1) },
      regional: { name: 'Referência Regional', percentage: this.apiData.penetratioN_REF_REG.toFixed(1) },
      national: { name: 'Referência Nacional', percentage: this.apiData.penetratioN_REF_NAC.toFixed(1) }
    };
  }

  get ratioUsadosData() {
    return this.apiData ? [
      this.apiData.raT_RN,
      this.apiData.raT_MED_REG,
      this.apiData.raT_MED_NAC
    ] : [0, 0, 0];
  }

  get ratioUsadosLabels() {
    return [this.concessionariaNome, 'Média Regional', 'Média Nacional'];
  }

  get ratioUsadosColors() {
    return ['#001E50', '#00B0F0', '#6A767D'];
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }

  get primeiroCardValores() {
    if (!this.apiData) {
      return {
        concessionaria: 'R$ 0',
        mediaRegional: 'R$ 0',
        mediaNacional: 'R$ 0'
      };
    }

    return {
      concessionaria: this.formatCurrency(this.apiData.vaL_FINANC_ANO_ATUAL_DN),
      mediaRegional: this.formatCurrency(this.apiData.vaL_FINANC_ANO_ATUAL_REG),
      mediaNacional: this.formatCurrency(this.apiData.vaL_FINANC_ANO_ATUAL_NAC)
    };
  }

  get simpleChartLabels() {
    return [this.concessionariaNome, 'Média Regional', 'Média Nacional'];
  }

  get novosSimpleData() {
    if (!this.apiData) return [0, 0, 0];
    return [
      this.apiData.voL_NOVOS_VEIC_FINANC_DN,
      this.apiData.voL_NOVOS_VEIC_FINANC_REG,
      this.apiData.voL_NOVOS_VEIC_FINANC_NAC
    ];
  }

  get usadosSimpleData() {
    if (!this.apiData) return [0, 0, 0];
    return [
      this.apiData.voL_USADOS_VEIC_FINANC_DN,
      this.apiData.voL_USADOS_VEIC_FINANC_REG,
      this.apiData.voL_USADOS_VEIC_FINANC_NAC
    ];
  }

  get simpleBarsColors() {
    return ['#001E50', '#00B0F0', '#6A767D'];
  }

  // Método para obter o ano do exercício selecionado
  getSelectedYearText(): string {
    if (this.selectedYear !== null) {
      return this.selectedYear.toString();
    } else {
      // Se não houver ano selecionado, use o ano atual
      return new Date().getFullYear().toString();
    }
  }

  // No componente pai
  onExportDirect(): void {
    // Captura o container completo (com filtros) para o PDF. O popup é fechado antes pelo export-document-button (setTimeout).
    const el = this.reportContainer?.nativeElement;
    if (!el) return;

    const pageTitle = typeof this.reportPageContext?.getTitle === 'function'
      ? this.reportPageContext.getTitle()
      : 'Raio-X-Dealer';
    const y = this.selectedYear ?? new Date().getFullYear();
    const m = this.selectedMonth ?? new Date().getMonth() + 1;

    html2canvas(el, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: true,
      ignoreElements: (element) => {
        if (element.tagName === 'IFRAME') return true;
        const el = element as HTMLElement;
        if (el.closest?.('.type-picker-selector') || el.closest?.('.export-selector')) return true;
        return false;
      },
      onclone: (_clonedDoc, clonedElement) => {
        clonedElement.style.overflow = 'visible';
        clonedElement.style.display = 'block';
      },
    }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvas.width, canvas.height],
      });
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      const safeTitle = pageTitle.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9\-]/g, '') || 'Raio-X-Dealer';
      const fileName = `${safeTitle}-${y}-${m}.pdf`;
      pdf.save(fileName);
    }).catch((err) => console.error('Erro ao gerar PDF:', err));
  }

  handleExportModalDownload() {
    if (this.exportFileBlob && this.exportFileName) {
      const objectUrl = window.URL.createObjectURL(this.exportFileBlob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = this.exportFileName;
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(objectUrl);
    }
    this.showExportModal = false;
  }

  // Exemplo de uso para exportação PDF
  exportPdfModal() {
    const el = this.reportContainer?.nativeElement;
    if (!el) return;
    const pageTitle = typeof this.reportPageContext?.getTitle === 'function'
      ? this.reportPageContext.getTitle()
      : 'Raio-X-Dealer';
    const y = this.selectedYear ?? new Date().getFullYear();
    const m = this.selectedMonth ?? new Date().getMonth() + 1;
    html2canvas(el, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      allowTaint: true,
      ignoreElements: (element) => {
        if (element.tagName === 'IFRAME') return true;
        const el = element as HTMLElement;
        if (el.closest?.('.type-picker-selector') || el.closest?.('.export-selector')) return true;
        return false;
      },
      onclone: (_clonedDoc, clonedElement) => {
        clonedElement.style.overflow = 'visible';
        clonedElement.style.display = 'block';
      },
    }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvas.width, canvas.height],
      });
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      const safeTitle = pageTitle.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9\-]/g, '') || 'Raio-X-Dealer';
      const fileName = `${safeTitle}-${y}-${m}.pdf`;
      const blob = pdf.output('blob');
      this.exportFileName = fileName;
      this.exportFileBlob = blob;
      this.exportEmailSubject = `Relatório Raio-X Dealer - ${y}/${m}`;
      this.exportEmailBody = `Segue em anexo o relatório Raio-X Dealer referente ao período ${m}/${y}.`;
      this.showExportModal = true;
    }).catch((err) => console.error('Erro ao gerar PDF:', err));
  }

  // MÉTODO PARA EXPORTAÇÃO EXCEL E ABERTURA DO MODAL
  exportExcelModal(period: { start: { year: number; month: number }; end: { year: number; month: number } }): void {
    const dn = this.selectedDnService.getSelectedDnAsNumber();
    if (dn == null || dn === undefined || Number.isNaN(dn)) {
      this.errorMessage = 'Selecione um DN para exportar.';
      this.cdr.detectChanges();
      return;
    }
    this.exportLoading = true;
    this.errorMessage = null;
    this.cdr.detectChanges();
    this.xRayDealerService.getDealerExportData(period, dn).subscribe({
      next: (res) => {
        try {
          const pad = (n: number) => n.toString().padStart(2, '0');
          const allRows = (res.data || []).flatMap((d: { exercicio: string; items: any[] }) =>
            (d.items || []).map((item: any) => ({ Exercicio: d.exercicio, ...item }))
          );
          const wb = XLSX.utils.book_new();
          const ws = XLSX.utils.json_to_sheet(allRows);
          XLSX.utils.book_append_sheet(wb, ws, 'Raio-X-Dealer');
          const fileName = `raio-x-dealer-${dn}-${period.start.year}${pad(period.start.month)}-${period.end.year}${pad(period.end.month)}.xlsx`;
          const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
          const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          this.exportFileName = fileName;
          this.exportFileBlob = blob;
          this.exportEmailSubject = `Relatório Raio-X Dealer - ${period.start.month}/${period.start.year} a ${period.end.month}/${period.end.year}`;
          this.exportEmailBody = `Segue em anexo o relatório Raio-X Dealer referente ao período ${period.start.month}/${period.start.year} a ${period.end.month}/${period.end.year}.`;
          this.showExportModal = true;
        } catch (e) {
          console.error('Erro ao gerar Excel:', e);
          this.errorMessage = 'Erro ao gerar o arquivo Excel. Tente novamente.';
          this.cdr.detectChanges();
        }
        this.exportLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Erro ao exportar dados:', err);
        this.errorMessage = err?.error?.message || 'Erro ao buscar dados para exportação. Tente novamente.';
        this.exportLoading = false;
        this.cdr.detectChanges();
      }
    });
  }
}
